import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const MODALITIES = [
  {
    name: "X-RAY",
    desc: "High-throughput chest and skeletal screening with 99.1% negative predictive value.",
    acc: "99.1%",
    img: "https://images.unsplash.com/photo-1616012481039-5de1dcb42934?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwzfHxtcmklMjBzY2FufGVufDB8fHx8MTc4OTA3MTg4Nnww&ixlib=rb-4.1.0&q=85",
    span: "md:col-span-7",
  },
  {
    name: "CT",
    desc: "Volumetric analysis of 600+ slices per study. Nodule detection down to 3mm.",
    acc: "98.7%",
    img: "https://images.unsplash.com/photo-1631563019676-dade0dbdb8fc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwzfHxicmFpbiUyMHNjYW4lMjBjb21wdXRlcnxlbnwwfHx8fDE3ODkwNzE5MDV8MA&ixlib=rb-4.1.0&q=85",
    span: "md:col-span-5",
  },
  {
    name: "MRI",
    desc: "Multi-sequence soft-tissue characterization for neuro and breast oncology.",
    acc: "98.9%",
    img: "https://images.unsplash.com/photo-1666214275099-0ca566aefe26?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwyfHxkb2N0b3IlMjBhbmFseXppbmclMjBtcmklMjBzY2FufGVufDB8fHx8MTc4OTEwMjUwMnww&ixlib=rb-4.1.0&q=85",
    span: "md:col-span-5",
  },
  {
    name: "ULTRASOUND",
    desc: "Real-time elastography and TI-RADS / BI-RADS scoring at the point of care.",
    acc: "97.4%",
    img: "https://images.unsplash.com/photo-1781707401802-b479c406b1af?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMG1lZGljYWwlMjB0ZWNobm9sb2d5fGVufDB8fHx8MTc4OTA3MTkwNXww&ixlib=rb-4.1.0&q=85",
    span: "md:col-span-7",
  },
];

export default function Modalities() {
  return (
    <section id="modalities" className="bg-surface py-24 md:py-32 border-t border-slate-200" data-testid="modalities-section">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7 }}
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tighter leading-none"
          >
            Every modality.
            <br />
            <span className="text-mute">One intelligence.</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="max-w-sm text-mute text-base leading-relaxed"
          >
            A single DICOM-native pipeline ingests studies from any scanner, any vendor, and returns structured findings in seconds.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {MODALITIES.map((m, i) => (
            <motion.div
              key={m.name}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.08 }}
              className={`${m.span} group relative border border-slate-200 bg-white rounded-2xl overflow-hidden hover:border-brand/60 hover:shadow-[0_4px_20px_rgba(15,23,42,0.06)] hover:-translate-y-1 transition-[border-color,box-shadow,transform] duration-500`}
              data-testid={`modality-card-${m.name.toLowerCase().replace("-", "")}`}
            >
              <div className="relative h-56 overflow-hidden">
                <img
                  src={m.img}
                  alt={`${m.name} imaging`}
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-[filter,transform] duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink to-transparent" aria-hidden="true" />
                <span className="absolute top-4 right-4 font-mono text-[10px] uppercase tracking-[0.2em] bg-white/80 backdrop-blur px-2 py-1 text-brand">
                  ACC {m.acc}
                </span>
              </div>
              <div className="p-8 flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-display text-2xl font-bold tracking-tight mb-2">{m.name}</h3>
                  <p className="text-mute text-sm leading-relaxed max-w-md">{m.desc}</p>
                </div>
                <ArrowUpRight className="w-5 h-5 text-mute group-hover:text-brand group-hover:translate-x-1 group-hover:-translate-y-1 transition-[color,transform] duration-300 shrink-0 mt-1" />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
